// FIG Static PE (DESIGN_SPEC §11.4.10, RESTYLE §1–2). Props: FigProps. Three journal cells, one row:
//   s3 lead = four readouts · s5 = the decoded header sheet and section entropy
//   (value first, never clipped; the packed threshold ticked at 7.2; hot sections tinted) · s4 side = annotated
//   strings, interesting first, with static-string IOC false positives labelled.
//   meta(ctx) → { id: "static", label: "Static PE", count: sections, empty: no static-report artifact (URL runs, non-PE samples) }
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Alert, CopyText, FigHead, Loading, Readouts, Sep, Sheet, figTitleId, type SheetRow } from "@/components/ui";
import { cx } from "@/components/ui/cx";
import { useStaticReport } from "@/hooks";
import { isApiError } from "@/lib/api";
import { bytes, count } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import type { Event, StaticReport, StaticSection } from "@/types";
import {
  PACKED,
  annotateStrings,
  characteristics,
  entryPoint,
  importsOf,
  machineWords,
  peKind,
  sectionPerms,
  stamp,
  subsystemWords,
  type StringNote,
} from "../derive/pe-decode";

export function meta(ctx: ReportCtx): FigMeta {
  const has = !!ctx.staticReport || ctx.artifacts.some((a) => a.kind === "static-report");
  return { id: "static", label: "Static PE", count: ctx.staticReport?.sections?.length || undefined, empty: !has };
}

const NAME = (s: StaticSection) => (s.name || "").replace(/\0+$/, "") || "(unnamed)";

/** Names and addresses the run itself touched, for "resolved at run time" / "contacted". */
function runNames(events: readonly Event[]): { hosts: string[]; ips: string[] } {
  const hosts = new Set<string>();
  const ips = new Set<string>();
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  for (const e of events) {
    const f = e.fields ?? {};
    if (e.kind === "net.dns") {
      const q = str(f.qname ?? f.name).toLowerCase().replace(/\.$/, "");
      if (q) hosts.add(q);
      const ip = str(f.ip);
      if (ip) ips.add(ip);
    } else if (e.kind === "net.http") {
      const h = str(f.host).toLowerCase().replace(/:\d+$/, "");
      if (h) hosts.add(h);
    } else if (e.kind === "net.flow" || e.kind === "net.conn") {
      const d = str(f.dst).replace(/:\d+$/, "");
      if (d) ips.add(d);
    }
  }
  return { hosts: [...hosts], ips: [...ips] };
}

// ---- header sheet ----------------------------------------------------------------------------------------------
function Mute({ children }: { children: ReactNode }) {
  return <span className="text-ink-mute">{children}</span>;
}

const IMPORT_DLLS = 6;

function ImportsValue({ r }: { r: StaticReport }) {
  const imp = useMemo(() => importsOf(r), [r]);
  const [all, setAll] = useState(false);
  if (!imp.dlls.length) return <Mute>none · {imp.note}</Mute>;
  const shown = all ? imp.dlls : imp.dlls.slice(0, IMPORT_DLLS);
  return (
    <span className="grid gap-1">
      {shown.map((d) => (
        <span key={d.dll} className="min-w-0">
          <span translate="no">{d.dll}</span>
          {d.fns.length > 0 && (
            <>
              <span className="text-ink-faint"> › </span>
              <span translate="no">{(all ? d.fns : d.fns.slice(0, 3)).join(", ")}</span>
              {!all && d.fns.length > 3 && <Mute> +{count(d.fns.length - 3)}</Mute>}
            </>
          )}
        </span>
      ))}
      {imp.note && <Mute>{imp.note}</Mute>}
      {(imp.dlls.length > IMPORT_DLLS || imp.dlls.some((d) => d.fns.length > 3)) && (
        <button type="button" className="lnk justify-self-start font-mono text-meta" onClick={() => setAll((v) => !v)}>
          {all ? "show fewer" : `show all ${count(imp.fnCount)} functions in ${count(imp.dlls.length)} ${imp.dlls.length === 1 ? "DLL" : "DLLs"}`}
        </button>
      )}
    </span>
  );
}

function headerRows(r: StaticReport, submittedMs: number): SheetRow[] {
  const ch = characteristics(r.characteristics);
  const ep = entryPoint(r);
  const ts = stamp(r.timestamp_unix, submittedMs);
  const sub = subsystemWords(r.subsystem || "");
  const rows: SheetRow[] = [
    {
      k: "machine",
      v: (
        <>
          <span translate="no">{r.machine || "unknown"}</span>
          <Sep />
          {peKind(r.machine || "")}
          <Mute> · {machineWords(r.machine || "")}</Mute>
        </>
      ),
    },
    {
      k: "subsystem",
      v: (
        <>
          <span translate="no">{r.subsystem || "unknown"}</span>
          {(sub || r.is_dll || r.is_driver) && (
            <Mute>
              {sub && ` · ${sub}`}
              {r.is_driver ? " · driver" : r.is_dll ? " · DLL" : ""}
            </Mute>
          )}
        </>
      ),
    },
    {
      k: "characteristics",
      v: (
        <span title={ch.flags.map((f) => `IMAGE_FILE_${f.name}`).join("\n") || undefined}>
          <span translate="no">{ch.hex}</span>
          {ch.flags.length > 0 && <Mute> · {ch.flags.map((f) => f.words).join(" · ")}</Mute>}
        </span>
      ),
    },
    {
      k: "entry point",
      v: (
        <>
          <span translate="no">{ep.hex}</span>
          <Mute> · {ep.note}</Mute>
        </>
      ),
    },
    {
      k: "timestamp",
      v: ts.iso ? (
        <>
          <time dateTime={ts.iso} title={ts.iso}>
            {ts.iso.replace("T", " ").replace(/\.\d{3}Z$/, "")} UTC
          </time>
          {ts.flag && <span className="dagger"> †</span>}
          {ts.note && <Mute> · {ts.note}</Mute>}
        </>
      ) : (
        <Mute>{ts.note}</Mute>
      ),
    },
    { k: "imports", v: <ImportsValue r={r} /> },
    {
      k: "imphash",
      v: r.imphash ? <span translate="no">{r.imphash}</span> : <Mute>none · no import table to hash</Mute>,
      copy: r.imphash ? { value: r.imphash, label: "imphash" } : undefined,
    },
    {
      k: "signer",
      v: r.signer?.present ? (
        <>
          Authenticode signature present<Mute> · {r.signer.note || "chain not verified"}</Mute>
        </>
      ) : (
        <Mute>unsigned · no Authenticode directory in the header</Mute>
      ),
    },
  ];
  return rows;
}

// ---- section entropy -----------------------------------------------------------------------------------------
// Fixed columns: every row is its own grid, so an auto column would misalign the bars and the 7.2 tick.
const COLS = "grid-cols-[8ch_5ch_minmax(80px,1fr)_12ch] max-[480px]:grid-cols-[7ch_4.5ch_minmax(60px,1fr)_4.5ch]";
function Sections({ sections }: { sections: readonly StaticSection[] }) {
  if (!sections.length) return <p className="note mt-2">No sections</p>;
  return (
    <div className="mt-1" role="table" aria-label="Section entropy, bits per byte">
      <div role="row" className="sr-only">
        <span role="columnheader">section</span>
        <span role="columnheader">entropy</span>
        <span role="columnheader">bar</span>
        <span role="columnheader">permissions and raw size</span>
      </div>
      {sections.map((s, i) => {
        const e = Math.max(0, Math.min(8, Number(s.entropy) || 0));
        const hot = e >= PACKED;
        const p = sectionPerms(s.characteristics);
        const unpacked = Number(s.raw_size) === 0 && Number(s.virtual_size) > 0;
        return (
          <div
            key={`${NAME(s)}:${i}`}
            role="row"
            className={cx("ent", COLS, hot && "bg-medium-tint")}
            title={`${NAME(s)}: ${e.toFixed(2)} bits per byte · ${p.perms}${p.words ? ` · ${p.words}` : ""} · raw ${bytes(s.raw_size)}, virtual ${bytes(s.virtual_size)}`}
          >
            <span role="cell" className={cx("nm truncate pl-1.5", hot && "font-semibold")} translate="no">
              {NAME(s)}
            </span>
            <span role="cell" className={cx("val", hot && "font-semibold text-medium-text")}>
              {e.toFixed(2)}
            </span>
            <span role="cell" className="ent-bar" aria-hidden="true">
              <b className={cx(hot && "hot bg-medium")} style={{ "--e": e } as CSSProperties} />
              <i className="thr" />
            </span>
            {/* text on the gold tint takes its text step (ink-mute on it is under 4.5:1) */}
            <span role="cell" className={cx("flex items-center justify-end gap-2 pr-1.5", hot ? "text-medium-text" : "text-ink-mute")}>
              {p.rwx ? <span className="tchip tchip--sus" title="writable and executable">rwx</span> : <span translate="no">{p.perms}</span>}
              <span className="max-[480px]:hidden">{unpacked ? "raw 0" : bytes(s.raw_size)}</span>
            </span>
          </div>
        );
      })}
      <div className={cx("grid gap-3 pt-1.5 font-mono text-data text-ink-faint", COLS)} aria-hidden="true">
        <span />
        <span />
        <span className="relative h-3 text-micro">
          <span className="absolute left-0 -translate-x-1/2">0</span>
          <span className="absolute left-1/2 -translate-x-1/2">4</span>
          <span className="absolute left-[90%] -translate-x-1/2 text-ink-mute">{PACKED}</span>
        </span>
        <span />
      </div>
    </div>
  );
}

// ---- strings -------------------------------------------------------------------------------------------------
const OF_INTEREST = 14;

function pickInteresting(notes: readonly StringNote[]): { shown: StringNote[]; folded: { hex: number; entropy: number; other: number } } {
  const shown: StringNote[] = [];
  let hex = 0;
  let entropy = 0;
  for (const n of notes) {
    if (shown.length >= OF_INTEREST) break;
    if (n.rank <= 2) shown.push(n);
    else if (n.kind === "hex" && hex < 2) {
      shown.push(n);
      hex++;
    } else if (n.kind === "entropy" && entropy < 3) {
      shown.push(n);
      entropy++;
    }
  }
  const set = new Set(shown);
  const rest = notes.filter((n) => !set.has(n));
  return {
    shown,
    folded: {
      hex: rest.filter((n) => n.kind === "hex").length,
      entropy: rest.filter((n) => n.kind === "entropy").length,
      other: rest.filter((n) => n.kind !== "hex" && n.kind !== "entropy").length,
    },
  };
}

function StringRow({ n }: { n: StringNote }) {
  return (
    <li className="grid-cols-1 gap-0.5">
      <span translate="no" spellCheck={false}>
        {n.s}
      </span>
      {(n.why || n.ioc) && (
        <span className="why whitespace-normal">
          {n.why}
          {n.ioc?.falsePositive && n.kind !== "namespace" && <> · listed as a {n.ioc.kind} IOC, though it is not a host</>}
          {n.ioc && !n.ioc.falsePositive && !n.why.includes("IOC") && <> · listed as a {n.ioc.kind} IOC</>}
        </span>
      )}
    </li>
  );
}

function Strings({ notes, picked, all }: { notes: readonly StringNote[]; picked: ReturnType<typeof pickInteresting>; all: readonly string[] }) {
  const [more, setMore] = useState(0);
  const { shown, folded } = picked;
  const total = notes.length;
  const rest = useMemo(() => {
    const set = new Set(shown);
    return notes.filter((n) => !set.has(n));
  }, [notes, shown]);
  const extra = rest.slice(0, more);
  const left = rest.length - extra.length;
  if (!notes.length) return <p className="note mt-2">No strings of interest</p>;
  return (
    <>
      <ul className="strings">
        {shown.map((n) => (
          <StringRow key={n.s} n={n} />
        ))}
        {extra.map((n) => (
          <StringRow key={n.s} n={n} />
        ))}
      </ul>
      <div className="hidden-note flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {left > 0 && (
          <span>
            {more === 0
              ? [
                  folded.entropy && `${count(folded.entropy)} more high-entropy`,
                  folded.hex && `${count(folded.hex)} more hex`,
                  folded.other && `${count(folded.other)} ${folded.entropy || folded.hex ? "others" : "more"}`,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : `${count(left)} more`}
          </span>
        )}
        {left > 0 && (
          <button type="button" onClick={() => setMore((m) => m + 50)}>
            show {count(Math.min(50, left))} more
          </button>
        )}
        {left > 50 && (
          <button type="button" onClick={() => setMore(rest.length)}>
            show all {count(total)}
          </button>
        )}
        <CopyText value={all.join("\n")} label="strings" className="ml-auto whitespace-nowrap">
          copy all {count(all.length)}
        </CopyText>
      </div>
    </>
  );
}

// ---- the figure ----------------------------------------------------------------------------------------------
export default function StaticPeFig({ ctx, no }: FigProps) {
  const q = useStaticReport(ctx.job.id, ctx.artifacts);
  const r = ctx.staticReport ?? q.data;
  const events = ctx.stream.snap.events;
  const version = ctx.stream.snap.version;
  const names = useMemo(() => runNames(events), [events, version]);
  const notes = useMemo(
    () => (r ? annotateStrings(r.strings, { hosts: names.hosts, ips: names.ips, iocs: ctx.behaviorReport?.iocs }) : []),
    [r, names, ctx.behaviorReport],
  );
  const submitted = Date.parse(ctx.job.submitted_at);

  if (!r) {
    return (
      <section className="cell fig" aria-labelledby={figTitleId(no)}>
        <FigHead no={no} label="Static PE" count="pre-run" />
        <div className="fig-body">
          {q.error ? (
            <Alert head="Couldn't read static-report.json">
              {isApiError(q.error) ? `${q.error.status} from ${q.error.path}${q.error.detail ? `: ${q.error.detail}` : ""}` : (q.error as Error).message}
            </Alert>
          ) : (
            <Loading verb="Loading">static-report.json</Loading>
          )}
        </div>
      </section>
    );
  }

  const sections = Array.isArray(r.sections) ? r.sections : [];
  const top = [...sections].sort((a, b) => (Number(b.entropy) || 0) - (Number(a.entropy) || 0))[0];
  const imp = importsOf(r);
  const picked = pickInteresting(notes);
  const interest = picked.shown.length;
  const listed = Array.isArray(r.strings) ? r.strings.length : 0;

  return (
    <>
      <section className="cell s3 lead fig" aria-labelledby={figTitleId(no)}>
        <FigHead no={no} label="Static PE" count="pre-run" />
        <Readouts
          className="mt-6 grid-cols-2"
          label="Static PE readouts"
          items={[
            { label: "max entropy", value: top ? (Number(top.entropy) || 0).toFixed(2) : "—", unit: top ? NAME(top) : undefined },
            { label: "sections", value: sections.length },
            { label: "imports", value: imp.fnCount, unit: imp.dlls.length === 1 ? imp.dlls[0].dll : imp.dlls.length ? `${imp.dlls.length} DLLs` : undefined },
            { label: "strings", value: listed, unit: listed ? `${count(interest)} of note` : undefined },
          ]}
        />
      </section>
      <section className="cell s5 fig" aria-label="Static PE: header and sections">
        <p className="subhead flex-wrap">
          Header
        </p>
        <Sheet rows={headerRows(r, submitted)} className="min-[481px]:grid-cols-[minmax(132px,30%)_minmax(0,1fr)]" />
        <p className="subhead mt-8 flex-wrap">
          Section entropy <span className="ct">bits per byte, 0–8 · packed at {PACKED}</span>
        </p>
        <Sections sections={sections} />
      </section>
      <section className="cell s4 side fig" aria-label="Static PE: strings">
        <p className="subhead flex-wrap">
          Strings{" "}
          <span className="ct">
            {count(interest)} of interest · {count(listed)} kept
          </span>
        </p>
        <Strings notes={notes} picked={picked} all={r.strings ?? []} />
      </section>
    </>
  );
}
